/**
 * The iframe's half of "the site's player signs in" (ADR-0249 §4, P2-127).
 *
 * THE IFRAME VERIFIES NOTHING. The JWT is signed by the site's backend with a
 * secret only our API holds a copy of; this side carries it from the loader's
 * message to `POST /v1/widget/session` and reports the outcome back as a
 * `done { n, ok, r }`. Every function here is about carrying and reporting —
 * narrowing an untrusted message, mapping an API answer to one of three reason
 * codes — and none of them is a security decision.
 *
 * Pure on purpose: the app's tests run in node without a DOM, so the decisions
 * live here and `host.ts` / `useWidget.ts` only wire them.
 */
import { ApiProblemError } from '../api/client';

/** The reason codes `done.r` may carry, and the loader's promise rejects with. */
export type IdentityFailure = 'identity_invalid' | 'identity_not_configured' | 'unavailable';

export type IdentityOutcome =
  { readonly ok: true } | { readonly ok: false; readonly reason: IdentityFailure };

export const IDENTITY_OK: IdentityOutcome = Object.freeze({ ok: true });

/** The contract's own bound on `userJwt`; anything longer the API would refuse anyway. */
const MAX_JWT_LENGTH = 4096;

/**
 * A JWT-shaped string from a host message, or null. Shape only — three
 * base64url segments — because a value that is not even shaped like a JWT is
 * a loader bug, and sending it would spend a rate-limited bootstrap to learn so.
 */
export function readJwt(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_JWT_LENGTH) {
    return null;
  }
  return /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*$/u.test(value) ? value : null;
}

/** The call number `n` that pairs a request with its `done`, or null. */
export function readCallNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

/**
 * The `sub` a JWT CLAIMS, read without verifying anything.
 *
 * Used for exactly one decision: whether the session this tab already holds was
 * minted for the same player, so a page navigation that re-sends `identify`
 * does not spend a bootstrap re-proving what the server proved a minute ago.
 * Trusting it for anything else would be trusting the page. A wrong answer here
 * costs one bootstrap, never another player's session — the server still checks
 * every JWT it is sent.
 */
export function jwtSubject(jwt: string): string | null {
  const payload = jwt.split('.')[1];
  if (payload === undefined || payload === '') {
    return null;
  }
  try {
    const base64 = payload.replace(/-/gu, '+').replace(/_/gu, '/');
    const binary = atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '='));
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
    if (typeof claims !== 'object' || claims === null) {
      return null;
    }
    const sub = (claims as { readonly sub?: unknown }).sub;
    return typeof sub === 'string' && sub !== '' ? sub : null;
  } catch (malformed) {
    if (malformed instanceof Error) {
      return null;
    }
    throw malformed;
  }
}

/**
 * What a failed sign-in or sign-out tells the site.
 *
 * The two identity codes pass through by name; everything else — a transport
 * failure, a 5xx, a refused key — is `unavailable`. Never a server message:
 * `done.r` lands in the customer's page, and our copy has no business there.
 */
export function identityFailure(cause: unknown): IdentityFailure {
  if (cause instanceof ApiProblemError) {
    if (cause.code === 'omni.widget.identity_invalid') {
      return 'identity_invalid';
    }
    if (cause.code === 'omni.widget.identity_not_configured') {
      return 'identity_not_configured';
    }
  }
  return 'unavailable';
}

/**
 * The edge refused the `Authorization` a sign-in carried — the anonymous token
 * this tab held had already expired or been revoked. Not the JWT's fault, and
 * worth exactly one retry without the header.
 */
export function isStaleBearer(cause: unknown): boolean {
  return cause instanceof ApiProblemError && cause.code === 'omni.auth.unauthenticated';
}
